import { useId, type ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { Link } from '@tanstack/react-router';
import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import {
  TASK_TYPE_DEFAULT_ASSIGNEE_ALLOCATIONS,
  TASK_TYPE_DEFAULT_ASSIGNEE_TYPES,
  type TaskTypeDefaultAssigneeAllocation,
} from '@erp/shared-types';
import useOrganizationMembers from 'src/api/organization/useOrganizationMembers';
import { useAllAgentDefinitions } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import { useUserGroupsList } from 'src/api/rpc/hooks/organization/useUserGroups';
import { useListActionWorkflowOptions } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import paths from 'src/paths';
import { Button } from 'src/components/ui/button';
import {
  SelectDropdown,
  type SelectDropdownOption,
} from 'src/components/common/SelectDropdown';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import { ToggleGroup, ToggleGroupItem } from 'src/components/ui/toggle-group';
import { FieldBlock } from './TaskTypeEditorSections';
import { assignableAgents } from './assignableAgents';
import type {
  TaskTypeDefaultAssigneeEditorState,
  TaskTypeDefaultAssigneeMode,
} from './taskTypeAdmin';

function isDefaultAssigneeMode(
  value: string,
): value is TaskTypeDefaultAssigneeMode {
  const kinds: readonly string[] = TASK_TYPE_DEFAULT_ASSIGNEE_TYPES;
  return value === 'none' || kinds.includes(value);
}

export function isAllocation(
  value: string,
): value is TaskTypeDefaultAssigneeAllocation {
  const allocations: readonly string[] = TASK_TYPE_DEFAULT_ASSIGNEE_ALLOCATIONS;
  return allocations.includes(value);
}

/**
 * Default assignee controls for a task type: a kind toggle (None / User /
 * Agent / Team / Workflow) and the matching picker — plus, for a team, the
 * allocation method that picks one member per task. A workflow is a reusable
 * workflow action that receives the task and returns the user to assign. Switching kinds keeps the other
 * kinds' last picks in draft state so toggling back and forth is not
 * destructive; `buildTaskTypeDefaultAssignee` drops the inactive ids on save.
 */
export function TaskTypeDefaultAssigneeField(
  props: TaskTypeDefaultAssigneeControlsProps,
) {
  return (
    <FieldBlock
      label={<T context="task type form field label">Default assignee</T>}
      description={
        <T context="hint under the default assignee controls on a task type">
          Tasks created with this type and no explicit assignee are assigned
          here — including tasks a workflow resolves for a record. An assignee
          set by the caller always wins. Changing it only affects future tasks.
        </T>
      }
    >
      <TaskTypeDefaultAssigneeControls {...props} />
    </FieldBlock>
  );
}

interface TaskTypeDefaultAssigneeControlsProps {
  editor: TaskTypeDefaultAssigneeEditorState;
  disabled?: boolean;
  onChange: (next: TaskTypeDefaultAssigneeEditorState) => void;
  /** Toggle and picker side by side (wrapping when narrow) instead of stacked. */
  inline?: boolean;
}

const STACKED_CLASS = 'flex flex-col gap-2';
const INLINE_ROW_CLASS = 'flex flex-wrap items-start gap-2';
const INLINE_PICKER_CLASS = 'flex min-w-[200px] flex-1 flex-col gap-2';
/** Inline: hairline item borders only (no group border or shadow), as in the AR Tasks mockup. */
const INLINE_TOGGLE_GROUP_CLASS =
  'w-fit border-0 data-[variant=outline]:shadow-none';
const INLINE_TOGGLE_ITEM_CLASS = 'border-border shadow-none';

/** The kind toggle and pickers alone, for surfaces with their own heading. */
export function TaskTypeDefaultAssigneeControls({
  editor,
  disabled,
  onChange,
  inline = false,
}: TaskTypeDefaultAssigneeControlsProps) {
  return (
    <div className={inline ? INLINE_ROW_CLASS : STACKED_CLASS}>
      <ToggleGroup
        type="single"
        value={editor.defaultAssigneeMode}
        onValueChange={(value) => {
          // Re-clicking the active item emits ''; keep a kind selected.
          if (isDefaultAssigneeMode(value)) {
            onChange({ ...editor, defaultAssigneeMode: value });
          }
        }}
        variant="outline"
        size="sm"
        disabled={disabled}
        className={inline ? INLINE_TOGGLE_GROUP_CLASS : 'w-fit'}
      >
        <ToggleGroupItem
          value="none"
          className={inline ? INLINE_TOGGLE_ITEM_CLASS : undefined}
        >
          <T context="default assignee kind toggle, no default">None</T>
        </ToggleGroupItem>
        <ToggleGroupItem
          value="user"
          className={inline ? INLINE_TOGGLE_ITEM_CLASS : undefined}
        >
          <T context="default assignee kind toggle, a person">User</T>
        </ToggleGroupItem>
        <ToggleGroupItem
          value="agent"
          className={inline ? INLINE_TOGGLE_ITEM_CLASS : undefined}
        >
          <T context="default assignee kind toggle, an AI agent">Agent</T>
        </ToggleGroupItem>
        <ToggleGroupItem
          value="user_group"
          className={inline ? INLINE_TOGGLE_ITEM_CLASS : undefined}
        >
          <T context="default assignee kind toggle, a user group whose members share the tasks">
            Team
          </T>
        </ToggleGroupItem>
        <ToggleGroupItem
          value="workflow"
          className={inline ? INLINE_TOGGLE_ITEM_CLASS : undefined}
        >
          <T context="default assignee kind toggle, a workflow that picks the assignee for each task">
            Workflow
          </T>
        </ToggleGroupItem>
      </ToggleGroup>

      {editor.defaultAssigneeMode !== 'none' && (
        <div className={inline ? INLINE_PICKER_CLASS : STACKED_CLASS}>
          <DefaultAssigneeTargetPicker
            editor={editor}
            disabled={disabled}
            onChange={onChange}
            size={inline ? 'default' : 'sm'}
          />
        </div>
      )}
    </div>
  );
}

/** The picker for the selected default-assignee kind. */
function DefaultAssigneeTargetPicker({
  editor,
  disabled,
  onChange,
  size,
}: Omit<TaskTypeDefaultAssigneeControlsProps, 'inline'> & {
  size: 'sm' | 'default';
}) {
  return (
    <>
      {editor.defaultAssigneeMode === 'user' && (
        <TaskTypeUserPicker
          size={size}
          value={editor.defaultAssigneeUserId}
          disabled={disabled}
          onChange={(defaultAssigneeUserId) =>
            onChange({ ...editor, defaultAssigneeUserId })
          }
        />
      )}

      {editor.defaultAssigneeMode === 'agent' && (
        <TaskTypeAgentPicker
          size={size}
          value={editor.defaultAssigneeAgentDefinitionId}
          disabled={disabled}
          onChange={(defaultAssigneeAgentDefinitionId) =>
            onChange({ ...editor, defaultAssigneeAgentDefinitionId })
          }
        />
      )}

      {editor.defaultAssigneeMode === 'user_group' && (
        <>
          <TaskTypeUserGroupPicker
            size={size}
            value={editor.defaultAssigneeUserGroupId}
            disabled={disabled}
            onChange={(defaultAssigneeUserGroupId) =>
              onChange({ ...editor, defaultAssigneeUserGroupId })
            }
          />
          <TaskTypeAllocationPicker
            value={editor.defaultAssigneeAllocation}
            disabled={disabled}
            onChange={(defaultAssigneeAllocation) =>
              onChange({ ...editor, defaultAssigneeAllocation })
            }
          />
        </>
      )}

      {editor.defaultAssigneeMode === 'workflow' && (
        <TaskTypeWorkflowPicker
          size={size}
          value={editor.defaultAssigneeWorkflowId}
          disabled={disabled}
          onChange={(defaultAssigneeWorkflowId) =>
            onChange({ ...editor, defaultAssigneeWorkflowId })
          }
        />
      )}
    </>
  );
}

export interface TaskTypePickerProps {
  value: string;
  disabled?: boolean;
  onChange: (id: string) => void;
  /** Dropdown height; `default` matches the kind toggle's height. */
  size?: 'sm' | 'default';
}

/** Shared picker shell: searchable single select + load-error line. */
function TaskTypeTargetPicker({
  value,
  disabled,
  size = 'sm',
  onChange,
  options,
  isLoading,
  loadError,
  placeholder,
  searchPlaceholder,
}: TaskTypePickerProps & {
  options: SelectDropdownOption[];
  isLoading: boolean;
  loadError: ReactNode | null;
  placeholder: string;
  searchPlaceholder: string;
}) {
  const selected = options.find((option) => option.id === value) ?? null;

  return (
    <div className="flex flex-col gap-1">
      <SelectDropdown
        options={options}
        value={selected}
        onChange={(option) => onChange(option?.id ?? '')}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        size={size}
        fullWidth
        disabled={disabled || isLoading}
      />
      {loadError ? (
        <p className="text-xs text-destructive">{loadError}</p>
      ) : null}
    </div>
  );
}

/**
 * Published workflow actions with exactly one output (the user to assign),
 * plus a link to open the chosen one. The server checks the rest: that a task
 * of this type can fill the workflow's inputs.
 */
function TaskTypeWorkflowPicker(props: TaskTypePickerProps) {
  const gt = useGT();
  const { data, isLoading, error } = useListActionWorkflowOptions();
  const options: SelectDropdownOption[] = (data?.workflows ?? []).flatMap(
    (workflow) =>
      workflow.interface.outputs.length === 1
        ? [
            {
              id: workflow.workflowId,
              label: workflow.name,
              display: workflow.name,
            },
          ]
        : [],
  );

  return (
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        <TaskTypeTargetPicker
          {...props}
          options={options}
          isLoading={isLoading}
          loadError={
            error ? (
              <T context="error loading workflows for the task type default assignee picker">
                Could not load workflows.
              </T>
            ) : null
          }
          placeholder={gt('Select a workflow…', {
            context:
              'placeholder for the task type default assignee workflow picker',
          })}
          searchPlaceholder={gt('Search workflows…', {
            context:
              'search box in the task type default assignee workflow picker',
          })}
        />
      </div>
      {props.value && (
        <Link
          to={paths.authenticated.workflows.workflow}
          params={{ workflowId: props.value }}
          target="_blank"
          className="shrink-0"
        >
          <Button variant="outline" size="icon" className="size-8">
            <span className="sr-only">
              <T context="button, open the default assignee workflow in a new tab">
                Open workflow
              </T>
            </span>
            <ArrowSquareOutIcon className="size-4" />
          </Button>
        </Link>
      )}
    </div>
  );
}

/** Active org members, for the default assignee and escalation target. */
export function TaskTypeUserPicker(props: TaskTypePickerProps) {
  const gt = useGT();
  const { data, isLoading, error } = useOrganizationMembers(true);
  const options: SelectDropdownOption[] = (data?.members ?? [])
    .filter((member) => member.deactivatedAt === null)
    .map((member) => ({
      id: member.userId,
      label: gt('{name} ({email})', {
        context: 'user option label combining display name and email',
        name: member.name,
        email: member.email,
      }),
      display: member.name,
    }));

  return (
    <TaskTypeTargetPicker
      {...props}
      options={options}
      isLoading={isLoading}
      loadError={
        error ? (
          <T context="error loading org members for the task type user picker">
            Could not load users.
          </T>
        ) : null
      }
      placeholder={gt('Select a user…', {
        context: 'placeholder for the task type user picker',
      })}
      searchPlaceholder={gt('Search users…', {
        context: 'search box in the task type user picker',
      })}
    />
  );
}

/** Agent definitions, for the default assignee. */
export function TaskTypeAgentPicker(props: TaskTypePickerProps) {
  const gt = useGT();
  const { data, isLoading, error } = useAllAgentDefinitions();
  const options: SelectDropdownOption[] = assignableAgents(
    data?.definitions ?? [],
    props.value,
  ).map((definition) => ({
    id: definition.id,
    label: definition.name,
    display: definition.name,
  }));

  return (
    <TaskTypeTargetPicker
      {...props}
      options={options}
      isLoading={isLoading}
      loadError={
        error ? (
          <T context="error loading AI agents for the task type agent picker">
            Could not load AI agents.
          </T>
        ) : null
      }
      placeholder={gt('Select an AI agent…', {
        context: 'placeholder for the task type agent picker',
      })}
      searchPlaceholder={gt('Search AI agents…', {
        context: 'search box in the task type agent picker',
      })}
    />
  );
}

/** User groups (teams), for the default assignee and escalation target pools. */
export function TaskTypeUserGroupPicker(props: TaskTypePickerProps) {
  const gt = useGT();
  const { data, isLoading, error } = useUserGroupsList();
  const options: SelectDropdownOption[] = (data ?? []).map((group) => ({
    id: group.id,
    label: group.name,
    display: group.name,
  }));

  return (
    <TaskTypeTargetPicker
      {...props}
      options={options}
      isLoading={isLoading}
      loadError={
        error ? (
          <T context="error loading user groups for the task type team picker">
            Could not load teams.
          </T>
        ) : null
      }
      placeholder={gt('Select a team…', {
        context: 'placeholder for the task type team (user group) picker',
      })}
      searchPlaceholder={gt('Search teams…', {
        context: 'search box in the task type team (user group) picker',
      })}
    />
  );
}

/**
 * How a team pool picks one member — per new task for the default assignee,
 * per hand-off for the escalation target. Both rotate on the same cursor (the
 * type's most recent assignee), so the copy stays neutral about which.
 */
export function TaskTypeAllocationPicker({
  value,
  disabled,
  onChange,
}: {
  value: TaskTypeDefaultAssigneeAllocation | '';
  disabled?: boolean;
  onChange: (allocation: TaskTypeDefaultAssigneeAllocation) => void;
}) {
  const idPrefix = useId();
  const gt = useGT();

  return (
    <RadioGroup
      value={value}
      disabled={disabled}
      onValueChange={(next) => {
        if (isAllocation(next)) onChange(next);
      }}
      aria-label={gt('Allocation method', {
        context:
          'accessible label for the picker choosing how a team pool assigns members',
      })}
      className="flex flex-col gap-2 rounded-md border border-border p-2.5"
    >
      <label
        htmlFor={`${idPrefix}-round-robin`}
        className="flex cursor-pointer gap-2.5 rounded-md p-1.5 hover:bg-muted/50"
      >
        <RadioGroupItem
          value="round_robin"
          id={`${idPrefix}-round-robin`}
          className="mt-0.5"
        />
        <span className="flex flex-col gap-0.5">
          <span className="block text-sm font-medium">
            <T context="team pool allocation option title">Round robin</T>
          </span>
          <span className="block text-xs leading-relaxed text-muted-foreground">
            <T context="explanation of the round robin allocation">
              Goes to the next member in turn, after whoever most recently
              received a task of this type.
            </T>
          </span>
        </span>
      </label>
      <label
        htmlFor={`${idPrefix}-least-open`}
        className="flex cursor-pointer gap-2.5 rounded-md p-1.5 hover:bg-muted/50"
      >
        <RadioGroupItem
          value="least_open_tasks"
          id={`${idPrefix}-least-open`}
          className="mt-0.5"
        />
        <span className="flex flex-col gap-0.5">
          <span className="block text-sm font-medium">
            <T context="team pool allocation option title">Fewest open tasks</T>
          </span>
          <span className="block text-xs leading-relaxed text-muted-foreground">
            <T context="explanation of the fewest open tasks allocation">
              Goes to the member with the fewest open tasks across the
              organization; ties rotate round robin.
            </T>
          </span>
        </span>
      </label>
    </RadioGroup>
  );
}
